import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import useResponsive from 'hooks/Navigation/useResponsive';
import useI18n from 'hooks/I18n/useI18n';

import { getLocaleByCode } from 'modules/utils/locales';
import { getChristmasLogoForEnabledCountries } from 'modules/logo';
import { currentAssemblySelector } from 'modules/navigation';
import { userSelector } from 'modules/currentUser';
import {
    showImmediately as showPanelImmediately,
    getVisiblePanel,
    HELP_PANEL,
} from 'modules/visiblePanel';
import environment from 'modules/utils/environment';

import { Link } from 'components/ui';
import AssemblyInfos from 'components/Navigation/ProductIdentity/AssemblyInfos.jsx';
import HelpButton from 'components/Navigation/ProductIdentity/HelpButton.jsx';
import AccountButton from 'components/Navigation/ProductIdentity/AccountButton.jsx';
import Button, { SMALL_SIZE } from 'src/components/atoms/Button/Button.jsx';
import GoogleOneTap from 'src/components/atoms/GoogleOneTap/GoogleOneTap.jsx';

const PublicHeader = ({ size }) => {
    const { i18n, trans } = useI18n();
    const locale = getLocaleByCode(i18n);
    const logo = getChristmasLogoForEnabledCountries(locale.logos.whiteOutlined, locale, true);
    const logoWithoutText = '/assets/new-design/images/icons/logo-without-text-white.svg';

    const isSmallWidth = useResponsive();
    const currentUser = useSelector(userSelector);
    const visiblePanel = useSelector(getVisiblePanel);
    const currentAssembly = useSelector(currentAssemblySelector);
    const { pathname } = useLocation();
    const dispatch = useDispatch();
    const farm = R.path(['farm', 0], currentUser);

    const [googleAuthPending, setGoogleAuthPending] = useState(false);
    const [authResolved, setAuthResolved] = useState(false);

    const displayHelpPanel = visiblePanel === HELP_PANEL;

    const isOnAssemblyHomepage = useMemo(() => pathname === '/' || pathname === '/assemblies', [
        pathname,
    ]);

    const openMenu = (e, panel) => {
        e.preventDefault();
        dispatch(showPanelImmediately(panel));
    };
    const openHelpMenu = e => openMenu(e, HELP_PANEL);
    useEffect(() => {
        document.body.classList.add('productIdentity');
    });

    const openHive = () => {
        window.location.href = trans('public.assembly.closed.openLinkURL');
    };
    useEffect(() => {
        if (currentUser !== undefined) {
            setAuthResolved(true);
        }
    }, [currentUser]);

    return (
        <div className={classNames('pi-public-header', size)}>
            {authResolved && !!currentUser.anonymous && !googleAuthPending && (
                <GoogleOneTap
                    clientId={process.env.GOOGLE_CLIENT_ID}
                    onCallPending={() => setGoogleAuthPending(true)}
                    onCallbackFail={() => setGoogleAuthPending(false)}
                    onCallbackSucceed={() => {
                        setGoogleAuthPending(false);
                        window.location.reload();
                    }}
                />
            )}
            <nav>
                {currentAssembly ? (
                    <AssemblyInfos />
                ) : (
                    <Link
                        href={
                            environment === 'production' ? `${locale.homepage}/${i18n}` : `/${i18n}`
                        }
                        data-bypass
                    >
                        {isSmallWidth ? (
                            <img
                                className="topNavigation-logo"
                                alt={trans('brand.name')}
                                src={logoWithoutText}
                            />
                        ) : (
                            <img
                                className="topNavigation-logo"
                                alt={trans('brand.name')}
                                src={logo}
                            />
                        )}
                    </Link>
                )}
                <div className="pi-public-header-right">
                    {
                        <HelpButton
                            currentAssembly={currentAssembly}
                            open={displayHelpPanel}
                            openHelpMenu={openHelpMenu}
                            currentUser={currentUser}
                            farm={farm}
                            productIdentity
                            fullHeight={isSmallWidth}
                        />
                    }
                    <AccountButton
                        isSmallWidth={isSmallWidth}
                        isOnHeader={isOnAssemblyHomepage}
                        shouldShowProductIdentity
                        fullHeight={isSmallWidth}
                    />
                    {!!currentUser.anonymous && (
                        <Button
                            onClick={openHive}
                            size={SMALL_SIZE}
                            className="pi-public-header-right-button"
                        >
                            {trans('header.open')}
                        </Button>
                    )}
                </div>
            </nav>
        </div>
    );
};

PublicHeader.propTypes = {
    size: PropTypes.string,
};

export default PublicHeader;
